import type React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Typography,
  alpha,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import { TestId } from "../constants/testIds";
import DetailHeaderSplitActions, {
  type DetailHeaderAction,
} from "../components/common/DetailHeaderSplitActions";
import DetailPageHeader from "../components/common/DetailPageHeader";
import FinancialSummaryCards from "../components/common/FinancialSummaryCards";
import StatusWorkflowStepper from "../components/common/StatusWorkflowStepper";
import OrderContextSummaryCards from "../components/orders/OrderContextSummaryCards";
import OrderHistorySection from "../components/orders/OrderHistorySection";
import SubmitRevisionDialog from "../components/orders/SubmitRevisionDialog";
import ExpenseChecklist from "../components/uangJalanReports/ExpenseChecklist";
import {
  deleteOrderApi,
  getOrderByIdApi,
  readyForReviewOrderApi,
  reopenOrderApi,
  submitRevisionOrderApi,
} from "../api/ordersApi";
import {
  calculateOrderExpenseTotal,
  getReadyForReviewMissingFieldKeys,
  isOrderApproved,
  isOrderEditable,
  isOrderReadyForReview,
  OrderStatus,
  type Order,
  splitDepartureTimestamp,
} from "../types/order";
import { canMutateOrders } from "../types/user";
import { buildOrderExpenseChecklistItems } from "../utils/buildOrderExpenseChecklistItems";
import { getErrorMessage } from "../utils/errorHandling";
import { getActiveRevisionNote } from "../utils/orderRevisionNotes";
import {
  getOrderWorkflowStepKey,
  getTripOrderDetailStepKey,
  ORDER_WORKFLOW_STEP_KEYS,
  shouldShowSuratJalanSteps,
  TRIP_ORDER_DETAIL_STEP_KEYS,
} from "../utils/orderStatusProgress";
import { toastEmitter } from "../utils/toastEmitter";
import type translations from "../translations/translations.json";

type TranslationKeys = keyof typeof translations.en;

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.user);
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [submitRevisionDialogOpen, setSubmitRevisionDialogOpen] = useState(false);

  const loadOrder = useCallback(async () => {
    if (!id) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const loadedOrder = await getOrderByIdApi(id);
      setOrder(loadedOrder);
    } catch (err: any) {
      setError(getErrorMessage(err, t, "fetchOrder"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const runAction = async (
    action: () => Promise<Order | undefined>,
    successMessage: TranslationKeys,
    context: string,
    onSuccess?: () => void,
  ) => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await action();
      if (result) {
        setOrder(result);
      }
      toastEmitter.emit(t(successMessage), "success");
      onSuccess?.();
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, t, context);
      setError(errorMessage);
      toastEmitter.emit(errorMessage, "error");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !order) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="error">{error || t("orders.notFound" as TranslationKeys)}</Typography>
      </Paper>
    );
  }

  if (!order) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography color="error">{t("orders.notFound" as TranslationKeys)}</Typography>
      </Paper>
    );
  }

  const userCanMutate = !!user && canMutateOrders(user);
  const canEdit = userCanMutate && isOrderEditable(order.status);
  const canReopen = userCanMutate && order.status === OrderStatus.READY_FOR_REVIEW;
  const canDelete = userCanMutate && order.status === OrderStatus.OPEN;
  const canSubmitRevision = userCanMutate && order.status === OrderStatus.REVISION_REQUIRED;
  const lockMissingFieldKeys = getReadyForReviewMissingFieldKeys(order);
  const canLockForReview = isOrderReadyForReview(order);
  const submitReviewDisabled =
    actionLoading || !userCanMutate || order.status !== OrderStatus.OPEN || !canLockForReview;

  const editDisabledHint = !userCanMutate
    ? t("permission.operationsOnly" as TranslationKeys)
    : !isOrderEditable(order.status)
      ? t("orders.actionDisabledNotEditable" as TranslationKeys)
      : "";

  const reopenDisabledHint = !userCanMutate
    ? t("permission.operationsOnly" as TranslationKeys)
    : order.status !== OrderStatus.READY_FOR_REVIEW
      ? t("orders.reopenDisabledStatus" as TranslationKeys)
      : "";

  const deleteDisabledHint = !userCanMutate
    ? t("permission.operationsOnly" as TranslationKeys)
    : order.status !== OrderStatus.OPEN
      ? t("orders.deleteDisabledStatus" as TranslationKeys)
      : "";

  const submitRevisionDisabledHint = !userCanMutate
    ? t("permission.operationsOnly" as TranslationKeys)
    : order.status !== OrderStatus.REVISION_REQUIRED
      ? t("orders.submitRevisionDisabledStatus" as TranslationKeys)
      : "";

  const getValidationLabel = (validationKey: string): string => {
    const [translationKey] = validationKey.split(":");
    return t(translationKey as TranslationKeys);
  };

  const lockForReviewTooltip =
    lockMissingFieldKeys.length > 0 ? (
      <Box component="span" sx={{ display: "block" }}>
        <Typography variant="caption" component="span" sx={{ display: "block", mb: 0.5 }}>
          {t("orders.lockMissingFieldsTitle" as TranslationKeys)}
        </Typography>
        {lockMissingFieldKeys.map((validationKey) => (
          <Typography
            key={validationKey}
            variant="caption"
            component="span"
            sx={{ display: "block" }}
          >
            • {getValidationLabel(validationKey)}
          </Typography>
        ))}
      </Box>
    ) : (
      ""
    );

  let submitReviewDisabledHint: React.ReactNode = "";
  if (!userCanMutate) {
    submitReviewDisabledHint = t("permission.operationsOnly" as TranslationKeys);
  } else if (order.status !== OrderStatus.OPEN) {
    submitReviewDisabledHint = t("orders.submitReviewDisabledStatus" as TranslationKeys);
  } else if (!canLockForReview) {
    submitReviewDisabledHint = lockForReviewTooltip;
  }

  const editAction: DetailHeaderAction = {
    id: "edit",
    label: t("Edit" as TranslationKeys),
    icon: <EditOutlinedIcon fontSize="small" />,
    onClick: () => navigate(`/orders/${order.readableId}/edit`),
    disabled: actionLoading || !canEdit,
    disabledHint: editDisabledHint,
    testId: TestId.ORDER.EDIT_BUTTON,
  };

  const lockAction: DetailHeaderAction = {
    id: "lock",
    label: t("orders.submitForReview" as TranslationKeys),
    icon: <LockOutlinedIcon fontSize="small" />,
    onClick: () =>
      runAction(
        () => readyForReviewOrderApi(order._id),
        "orders.submittedForReviewSuccessfully",
        "readyForReview",
      ),
    disabled: submitReviewDisabled,
    disabledHint: submitReviewDisabledHint,
    testId: TestId.ORDER.SUBMIT_REVIEW,
  };

  const reopenAction: DetailHeaderAction = {
    id: "reopen",
    label: t("orders.reopenForEditing" as TranslationKeys),
    icon: <ReplayOutlinedIcon fontSize="small" />,
    onClick: () =>
      runAction(() => reopenOrderApi(order._id), "orders.reopenedSuccessfully", "reopenOrder"),
    disabled: actionLoading || !canReopen,
    disabledHint: reopenDisabledHint,
    testId: TestId.ORDER.REOPEN,
  };

  const submitRevisionAction: DetailHeaderAction = {
    id: "submit-revision",
    label: t("orders.submitRevision" as TranslationKeys),
    icon: <SendOutlinedIcon fontSize="small" />,
    onClick: () => setSubmitRevisionDialogOpen(true),
    disabled: actionLoading || !canSubmitRevision,
    disabledHint: submitRevisionDisabledHint,
    testId: TestId.ORDER.SUBMIT_REVISION,
  };

  const deleteAction: DetailHeaderAction = {
    id: "delete",
    label: t("Delete" as TranslationKeys),
    icon: <DeleteOutlineIcon fontSize="small" />,
    onClick: () => setDeleteDialogOpen(true),
    disabled: actionLoading || !canDelete,
    disabledHint: deleteDisabledHint,
    testId: TestId.ORDER.DELETE,
  };

  const transitionActions = [submitRevisionAction, lockAction, reopenAction];
  const enabledTransition = transitionActions.find((action) => !action.disabled);
  const primary =
    enabledTransition ??
    (order.status === OrderStatus.REVISION_REQUIRED
      ? submitRevisionAction
      : order.status === OrderStatus.READY_FOR_REVIEW
        ? reopenAction
        : lockAction);

  const leadingActions = canEdit ? [editAction] : [];
  const menuActions = [
    ...(canEdit ? [] : [editAction]),
    ...transitionActions.filter((action) => action.id !== primary.id),
    deleteAction,
  ];

  const totalPaid = calculateOrderExpenseTotal(order);
  const totalVerified = order.totalApprovedCost;
  const difference = totalPaid - totalVerified;
  const checklistItems = buildOrderExpenseChecklistItems(order, t);
  const activeRevisionNote = getActiveRevisionNote(order);
  const showSuratJalanSteps = shouldShowSuratJalanSteps(order);
  const workflowStepKeys = showSuratJalanSteps
    ? TRIP_ORDER_DETAIL_STEP_KEYS
    : ORDER_WORKFLOW_STEP_KEYS;
  const workflowSteps = workflowStepKeys.map((key) => ({
    key,
    label: t(key as TranslationKeys),
  }));
  const departureDate = order.departureTimestamp
    ? splitDepartureTimestamp(order.departureTimestamp).date
    : "";

  return (
    <Box data-testid={TestId.ORDER.DETAIL} sx={{ pb: 4 }}>
      <DetailPageHeader
        compact
        title={t("orders.detailTitle" as TranslationKeys)}
        subtitle={
          <Box component="span" data-testid={TestId.ORDER.READABLE_ID}>
            {order.readableId}
          </Box>
        }
        statusLabel={t("Status" as TranslationKeys)}
        status={order.status}
        statusTestId={TestId.ORDER.STATUS}
        metaLine={
          <>
            {departureDate ? `${t("Departure" as TranslationKeys)}: ${departureDate}` : null}
            {order.teamOperations?.name
              ? `${departureDate ? " · " : ""}${order.teamOperations.name}`
              : null}
            {order.externalOrderId ? ` · ${order.externalOrderId}` : null}
          </>
        }
        progressSlot={
          <StatusWorkflowStepper
            compact
            steps={workflowSteps}
            activeStepKey={
              showSuratJalanSteps
                ? getTripOrderDetailStepKey(order)
                : getOrderWorkflowStepKey(order.status)
            }
          />
        }
        statusNote={
          order.status === OrderStatus.REVISION_REQUIRED && activeRevisionNote ? (
            <Paper
              variant="outlined"
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                borderColor: "warning.main",
                maxWidth: "100%",
              }}
              data-testid={TestId.ORDER.REVISION_NOTES}
            >
              <Typography
                variant="caption"
                color="warning.dark"
                display="block"
                sx={{ fontWeight: 700, mb: 0.25 }}
              >
                {t("orders.revisionNote" as TranslationKeys)}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                {activeRevisionNote}
              </Typography>
            </Paper>
          ) : undefined
        }
        actions={
          <DetailHeaderSplitActions
            leadingActions={leadingActions}
            primary={primary}
            menuActions={menuActions}
            menuTestId={TestId.ORDER.HEADER_ACTIONS_MENU}
            finalState={isOrderApproved(order.status)}
          />
        }
      />

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <OrderContextSummaryCards
        order={order}
        initialProofAttachmentId={searchParams.get("proof") ?? undefined}
        onProofViewerClose={() => {
          if (!searchParams.has("proof")) {
            return;
          }

          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete("proof");
          setSearchParams(nextParams, { replace: true });
        }}
      />

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          alignItems: "start",
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}
          data-testid={TestId.ORDER.EXPENSE_CHECKLIST}
        >
          <ExpenseChecklist
            orderId={order._id}
            items={checklistItems}
            disabled
            disabledHint={t("orders.checklistReadOnlyHint" as TranslationKeys)}
          />

          <FinancialSummaryCards
            totalPaid={totalPaid}
            totalVerified={totalVerified}
            difference={difference}
            totalPaidLabel={t("reports.orderTotalPaid" as TranslationKeys)}
            totalVerifiedLabel={t("reports.totalApproved" as TranslationKeys)}
            differenceLabel={t("reports.difference" as TranslationKeys)}
            totalPaidTestId={TestId.ORDER.TOTAL_PAID}
            totalVerifiedTestId={TestId.ORDER.TOTAL_APPROVED}
            differenceTestId={TestId.ORDER.DIFFERENCE}
            totalVerifiedDisplay={
              isOrderApproved(order.status) ? undefined : t("reports.pending" as TranslationKeys)
            }
            differenceDisplay={
              isOrderApproved(order.status) ? undefined : t("reports.pending" as TranslationKeys)
            }
            orientation="horizontal"
            sx={{ mb: 0 }}
          />

          {isOrderApproved(order.status) && order.overrideNotes && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                borderColor: "warning.main",
              }}
              data-testid={TestId.ORDER.OVERRIDE_NOTES}
            >
              <Typography
                variant="subtitle2"
                color="warning.dark"
                display="block"
                sx={{ mb: 0.5, fontWeight: 700 }}
              >
                {t("orders.overrideNotes" as TranslationKeys)}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500 }}>
                {order.overrideNotes}
              </Typography>
            </Paper>
          )}
        </Box>

        <OrderHistorySection
          histories={order.histories ?? []}
          revisionNote={order.revisionNote}
          sx={{ mt: 0, height: "100%" }}
        />
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        data-testid={TestId.COMMON.CONFIRM_DIALOG}
      >
        <DialogTitle>{t("Delete" as TranslationKeys)}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("orders.deleteConfirm" as TranslationKeys)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("Cancel" as TranslationKeys)}
          </Button>
          <Button
            color="error"
            variant="contained"
            data-testid={TestId.COMMON.CONFIRM_BUTTON}
            onClick={() => {
              setDeleteDialogOpen(false);
              runAction(
                async () => {
                  await deleteOrderApi(order._id);
                  return undefined;
                },
                "orders.deletedSuccessfully",
                "deleteOrder",
                () => navigate("/orders"),
              );
            }}
          >
            {t("Delete" as TranslationKeys)}
          </Button>
        </DialogActions>
      </Dialog>

      <SubmitRevisionDialog
        open={submitRevisionDialogOpen}
        loading={actionLoading}
        onClose={() => setSubmitRevisionDialogOpen(false)}
        onSubmit={async (note) => {
          await runAction(
            () => submitRevisionOrderApi(order._id, note),
            "orders.revisionSubmittedSuccessfully",
            "submitRevision",
            () => setSubmitRevisionDialogOpen(false),
          );
        }}
      />
    </Box>
  );
};

export default OrderDetailPage;
